import classNames from 'classnames';
import createDecorator from 'final-form-calculate';
import { array, bool, func, number, object, oneOf, shape, string } from 'prop-types';
import React, { Component } from 'react';
import { compose } from 'redux';
import { Modal } from '../../../components';
import config from '../../../config';
import { ReloveEditListingForm, ProfileAddressForm } from '../../../forms';
import { withViewport } from '../../../util/contextHelpers';
import { ensureCurrentUser, ensureListing } from '../../../util/data';
import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import { types as sdkTypes } from '../../../util/sdkLoader';
import { propTypes } from '../../../util/types';
import {
  LISTING_PAGE_PARAM_TYPES, LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_NEW
} from '../../../util/urlHelpers';

import moment from 'moment';

import { getIsZipCodeAvailable } from '../../../ducks/Address.duck'
import css from './ReloveEditListingWizard.css';

const { Money } = sdkTypes;

// const RELOVE_COMMISSION_PERCENTAGE = config.marketplaceCommission;
// const RELOVE_BASE_COMMISSION = config.marketplaceBaseCommission;

let RELOVE_COMMISSION_PERCENTAGE;
let RELOVE_BASE_COMMISSION;

const calculateProfit = createDecorator(
  {
    field: 'price',
    updates: {
      profit: (priceValue) => {
        if(priceValue){
          if(priceValue.amount < 32500){
            RELOVE_COMMISSION_PERCENTAGE = 0;
            RELOVE_BASE_COMMISSION = 75;
          }
          else if(priceValue.amount >= 32500){
            RELOVE_COMMISSION_PERCENTAGE = 0.185;
            RELOVE_BASE_COMMISSION = 15;
          }
        }
        return priceValue ? ('$' + Number((priceValue.amount * (1 - RELOVE_COMMISSION_PERCENTAGE) / 100) - RELOVE_BASE_COMMISSION).toFixed(2)) : null;
      }
    }
  }
);

// Create a new or edit listing through EditListingWizard
class ReloveEditListingWizard extends Component {
  constructor(props) {
    super(props);

    this.state = {
      draftId: null,
      showAddressModal: false,
      zipIsUnavailable: false
    };
    this.handlePublishListing = this.handlePublishListing.bind(this);
    this.handleAddressModalClose = this.handleAddressModalClose.bind(this);
  }

  handlePublishListing(id) {
    const { onPublishListingDraft, currentUser } = this.props;

    const addressNotSet = !currentUser.attributes.profile.privateData || !currentUser.attributes.profile.privateData.address;

    if (addressNotSet) {
      this.setState({
        draftId: id,
        showAddressModal: true,
      });
    } else {
      onPublishListingDraft(id);
    }
  }

  /* Removes stripe onboarding return URL check */
  componentDidMount() {
    // No Stripe onboarding return URL check needed anymore
  }

  handleAddressModalClose() {
    this.setState({ showAddressModal: false });
  }

  render() {
    const {
      id, // just a plain id
      className,
      rootClassName,
      params, // From router, page params
      listing, // From parent the listing, has data of current listing if already uploaded
      viewport, // From withViewport, idk
      intl, // From injectIntl, for translations
      images, // Loaded images
      errors, // object with all errors
      onManageDisableScrolling, // From UI duck, idk
      currentUser,
      onCreateListingDraft,
      onUpdateListing,
      onChange,
      onPublishListingDraft,
      newListingPublished,
      onImageUpload,
      onRemoveImage,
      updateInProgress,
      onUpdateImageOrder,
      imageOrder,
      onUpdateProfile,
      updateProfileInProgress,
      updateProfileError,
      hasListingPriceBeingDiscounted,
      ...rest
    } = this.props;

    const isNewURI = params.type === LISTING_PAGE_PARAM_TYPE_NEW;
    const isDraftURI = params.type === LISTING_PAGE_PARAM_TYPE_DRAFT;
    const isNewListingFlow = isNewURI || isDraftURI;
    const rootClasses = rootClassName || css.root;
    const classes = classNames(rootClasses, className);
    const currentListing = ensureListing(listing);

    const ensuredCurrentUser = ensureCurrentUser(currentUser);
    const currentUserLoaded = !!ensuredCurrentUser.id;

    const pathParams = params;
    const pathParamsUpdated = !!this.state.draftId && pathParams.slug === 'draft' ?
      {
        slug: 'draft',
        id: this.state.draftId.uuid,
        type: 'draft',
        edit: 'edit'
      } : pathParams;

    const { description, title, price, publicData } = currentListing.attributes;

    const imageIds = images => {
      return images ? images.map(img => img.imageId || img.id) : null;
    };

    const handleAddressSubmit = values => {
      const {
        address,
        addressExtNumber,
        addressIntNumber,
        addressNeighborhood,
        addressZip,
        addressState,
        addressCity,
      } = values;

      const profile = {
        privateData: {
          address: {
            address_line: address,
            ext_number: addressExtNumber,
            int_number: addressIntNumber,
            neighborhood: addressNeighborhood,
            zip: addressZip,
            state: addressState,
            city: addressCity
          }
        }
      };

      this.setState({ zipIsUnavailable: false });

      const self = this;
      getIsZipCodeAvailable(addressZip).then((response) => {
        if (response.available) {
          onUpdateProfile(profile)
            .then(() => {
              self.setState({ showAddressModal: false })
              self.handlePublishListing(self.state.draftId);
            })
            .catch(() => {
              // do nothing
            });
        } else {
          this.setState({ zipIsUnavailable: true });
        }
      }).catch((e) => {
        this.setState({ zipIsUnavailable: true });
      })
    };

    const onCompleteEditListingWizard = (updateValues) => {
      // Normalize images for API call
      const { images: updatedImages, ...otherValues } = updateValues;

      const imageProperty =
        typeof updatedImages !== 'undefined' ? { images: imageIds(updatedImages) } : {};
      const updateValuesWithImages = { ...otherValues, ...imageProperty };
      const self = this;
      if (isNewListingFlow) {
        const onUpsertListingDraft = isNewURI
          ? (updateValues) => onCreateListingDraft(updateValues)
          : onUpdateListing;
        const upsertValues = isNewURI
          ? updateValuesWithImages
          : { ...updateValuesWithImages, id: currentListing.id };

        onUpsertListingDraft(upsertValues)
          .then(r => {
            self.handlePublishListing(r.data.data.id);
          })
          .catch(e => {
            // No need for extra actions
          });
      } else {
        updateValuesWithImages.publicData['hasBeenUpdated'] = true;
        updateValuesWithImages.publicData['updateTime'] = moment().format('x');
        const upsertValues = isNewURI
          ? updateValuesWithImages
          : { ...updateValuesWithImages, id: currentListing.id };
        onUpdateListing(upsertValues);
      }
    };

    const {original_price, category, sold, /*available,*/ ...restPublicData } = publicData;

    const formatted_category = category && typeof category[0] != 'undefined' ? category[0] : null ;
    const formatted_subcategory = category && typeof category[1] != 'undefined' ? category[1] : null ;

    const price_formatted = original_price ?
    new Money(original_price.amount, original_price.currency)
    : null;

    const profit = price ? (Number((price.amount * (1 - RELOVE_COMMISSION_PERCENTAGE) / 100) - RELOVE_BASE_COMMISSION).toFixed(2) + ' MXN') : null;

    const formattedPublicData = {
      original_price: price_formatted,
      category: formatted_category,
      subcategory: formatted_subcategory,
      ...restPublicData
    };

    const { privateData } = ensuredCurrentUser.attributes.profile;

    const {
      address_line: address,
      city: addressCity,
      ext_number: addressExtNumber,
      int_number: addressIntNumber,
      neighborhood: addressNeighborhood,
      state: addressState,
      zip: addressZip,
    } = privateData ? privateData.address || {} : {};
    
    let soldValue = sold !== undefined ? sold : false;

    const maxPrice = hasListingPriceBeingDiscounted ? listing.attributes.price : false;

    return (
      <div className={classes}>
        <div className={css.sectionContainer}>
          <ReloveEditListingForm
            className={css.form}
            initialValues={{ 
              title, 
              description, 
              ...formattedPublicData, 
              price, 
              profit, 
              available: formattedPublicData.available ? formattedPublicData.available : 'Disponible'
            }}
            decorators={[calculateProfit]}
            saveActionMsg={isNewListingFlow ? 'Publicar' : 'Actualizar'}
            onSubmit={values => {
              const {
                title,
                description,
                price,
                original_price,
                size,
                category,
                subcategory,
                style,
                color,
                brand,
                use,
                available
              } = values;
              const categoryList = [category, subcategory];
              const sizeList = Array.isArray(size) ? size.map(item => typeof item === 'string'  ? item : item.key ) : [];
              const colorList = Array.isArray(color) ? color.map(item => typeof item === 'string'  ? item : item.key) : [];
              let availableValue = available !== undefined ? available : "Disponible";    
              const updateValues = {
                title: title.trim(),
                description,
                images,
                price,
                publicData: {
                  original_price: { amount: original_price.amount, currency: original_price.currency },
                  category: categoryList.filter(Boolean),
                  size: sizeList,
                  color: colorList,
                  style,
                  brand,
                  use,
                  shippingFee: { amount: 10000, currency: 'MXN' },
                  sold: soldValue,
                  available: availableValue,
                },
              };

              onCompleteEditListingWizard(updateValues);
            }}
            onChange={onChange}
            ready={newListingPublished}
            updated={false}
            disabled={false}
            keepDirtyOnReinitialize
            onImageUpload={onImageUpload}
            onRemoveImage={onRemoveImage}
            updateInProgress={updateInProgress}
            onUpdateImageOrder={onUpdateImageOrder}
            imageOrder={imageOrder}
            images={images}
            fetchErrors={errors}
            hasListingPriceBeingDiscounted={hasListingPriceBeingDiscounted}
            maxPrice={maxPrice}
          />
        </div>
        <Modal
          id="EditListingWizard.addressModal"
          isOpen={this.state.showAddressModal}
          onClose={this.handleAddressModalClose}
          onManageDisableScrolling={onManageDisableScrolling}
          usePortal
        >
          <div className={css.modalPayoutDetailsWrapper}>
            <h1 className={css.modalTitle}>
              Agrega tus datos
            </h1>
            {currentUserLoaded ? (
              <>
                <p className={css.modalMessage}>
                  Para poder calcular el costo de envío necesitamos saber tu dirección y tu número de telefono
                  para mejor comunicación.
                  Si necesitas cambiar los datos más adelante, lo puedes hacer desde tu Configuración de Perfil,
                  en la pestaña de Editar Dirección
                </p>
                <ProfileAddressForm
                  className={css.addressForm}
                  initialValues={{
                    address,
                    addressExtNumber,
                    addressIntNumber,
                    addressNeighborhood,
                    addressZip,
                    addressState,
                    addressCity,
                  }}
                  updateInProgress={updateProfileInProgress}
                  updateProfileError={updateProfileError}
                  zipIsUnavailable={this.state.zipIsUnavailable}
                  onManageDisableScrolling={this.props.onManageDisableScrolling}
                  onSubmit={handleAddressSubmit}
                />
              </>
            ) : null}
          </div>
        </Modal>
      </div>
    );
  }
}

ReloveEditListingWizard.defaultProps = {
  className: null,
  currentUser: null,
  rootClassName: null,
  listing: null,
  updateInProgress: false,
};

ReloveEditListingWizard.propTypes = {
  id: string.isRequired,
  className: string,
  currentUser: propTypes.currentUser,
  rootClassName: string,
  params: shape({
    id: string.isRequired,
    slug: string.isRequired,
    type: oneOf(LISTING_PAGE_PARAM_TYPES).isRequired,
    edit: string,
  }).isRequired,

  // We cannot use propTypes.listing since the listing might be a draft.
  listing: shape({
    attributes: shape({
      publicData: object,
      description: string,
      geolocation: object,
      pricing: object,
      title: string,
    }),
    images: array,
  }),

  errors: shape({
    createListingDraftError: object,
    updateListingError: object,
    publishListingError: object,
    showListingsError: object,
    uploadImageError: object,
  }).isRequired,

  // from withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default compose(
  withViewport,
  injectIntl
)(ReloveEditListingWizard);
